import React from 'react';
import axios from 'axios';
import {
  X, Package, FileText, Upload, Truck, Edit, Image as ImageIcon, Save,
  MessageSquare, History, RefreshCw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ProofThumbnail } from '../../components/ProofThumbnail';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  ['draft', 'Draft'],
  // iter114s.99 — full status coverage. The select previously lacked
  // several real statuses (paid, file_review, proof_sent, shipped…) so
  // it silently displayed 'Draft' for a PAID order — one careless
  // Update click away from regressing a live order.
  ['paid', 'Paid'],
  ['pending_review', 'Pending Review'],
  ['file_review', 'File Review'],
  ['artwork_uploaded', 'Artwork Uploaded'],
  ['proof_in_progress', 'Proof In Progress'],
  ['proof_pending', 'Proof Pending'],
  ['proof_sent', 'Proof Sent'],
  ['awaiting_approval', 'Awaiting Approval'],
  ['proof_approved', 'Proof Approved'],
  ['proof_rejected', 'Proof Rejected'],
  ['in_production', 'In Production'],
  ['shipped', 'Shipped'],
  ['completed', 'Completed'],
  ['delivered', 'Delivered'],
  // iter114s.91 — operator-requested. Lets admin mark a customer
  // request as cancelled without resorting to deletion (keeps the
  // audit trail + lets the customer see the resolution in their
  // dashboard). Backend already accepts arbitrary status strings
  // on the OrderStatusUpdate payload — no schema migration needed.
  ['cancelled', 'Order Cancelled'],
];

const DocRow = ({ label, icon: Icon, iconClass, href, btnClass, testId }) => (
  <div className="flex items-center justify-between bg-white p-3 rounded">
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${iconClass}`} />
      <span className="text-sm font-medium">{label}</span>
    </div>
    <a
      href={href}
      download
      data-testid={testId}
      className={`text-xs px-3 py-1 rounded text-white transition-colors ${btnClass}`}
    >
      Download
    </a>
  </div>
);

const OrderSummary = ({ order }) => (
  <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-[#4A7C59]">
    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
      <Package className="w-5 h-5 text-[#4A7C59]" />
      Order Summary
    </h3>
    {/* iter114s.74 — artwork+dieline thumbnail anchors the order
        summary so admins can identify the order by visual at a
        glance (matches the customer-side preview). Same lazy-
        loaded component, cached server-side per (order, item). */}
    <div className="flex gap-4">
      <ProofThumbnail
        orderId={order.id}
        size={120}
        rounded="lg"
        testId={`admin-modal-thumbnail-${order.id}`}
        alt={`Proof preview for ${order.order_number}`}
      />
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm flex-1 min-w-0">
        <div>
          <span className="text-gray-600">Customer:</span>
          <p className="font-semibold text-gray-800 truncate">{order.customer_email || 'N/A'}</p>
        </div>
        <div>
          <span className="text-gray-600">Total:</span>
          <p className="font-semibold text-gray-800">${order.total_price?.toFixed(2) || '0.00'}</p>
        </div>
        <div>
          <span className="text-gray-600">Status:</span>
          <p className="font-semibold text-gray-800">{order.status?.replace(/_/g, ' ').toUpperCase()}</p>
        </div>
        <div>
          <span className="text-gray-600">Items:</span>
          <p className="font-semibold text-gray-800">{order.items?.length || 0} item(s)</p>
        </div>
        {order.shipping_info?.purchase_order_number && (
          <div className="col-span-2">
            <span className="text-gray-600">PO Number:</span>
            <p className="font-semibold text-gray-800">{order.shipping_info.purchase_order_number}</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const GeneratedDocs = ({ order }) => {
  const hasAny = order.quote_id || order.invoice_id || order.auto_generated_proof_id;
  // Live-regenerated docs available for any order with artwork.
  // Unlike quote_id/invoice_id (which point to persisted file records),
  // the proof PDF and print file are rendered on demand from the live
  // order data — so they stay in sync even after artwork is swapped.
  const hasArtwork = (order.artwork_files || []).length > 0
    || (order.items || []).some((it) => (it.artwork_file_ids || []).length > 0);
  return (
    <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <FileText className="w-5 h-5 text-blue-600" />
        Generated Documents
      </h3>
      <div className="space-y-2">
        {order.quote_id && (
          <DocRow
            label="Quote PDF"
            icon={FileText}
            iconClass="text-blue-600"
            href={`${API_URL}/api/files/download/${order.quote_id}`}
            btnClass="bg-blue-500 hover:bg-blue-600"
          />
        )}
        {/* iter114s.93 — Use the dynamic `/order-confirmation-pdf`
            endpoint instead of `/api/files/download/{invoice_id}`. The
            static file is written to ephemeral pod disk; production
            runs N replicas so a regenerated PDF written on replica A
            404s when the download request lands on replica B. The
            dynamic endpoint renders on-the-fly from Mongo state, so
            every replica can serve it AND the result always reflects
            the latest order data (no stale snapshots). */}
        {order.id && (
          <DocRow
            label="Order Confirmation"
            icon={FileText}
            iconClass="text-green-600"
            href={`${API_URL}/api/enhanced-orders/${order.id}/order-confirmation-pdf`}
            btnClass="bg-green-500 hover:bg-green-600"
          />
        )}
        {order.auto_generated_proof_id && (
          <DocRow
            label="Auto-Generated Artwork Proof"
            icon={FileText}
            iconClass="text-purple-600"
            href={`${API_URL}/api/files/download/${order.auto_generated_proof_id}`}
            btnClass="bg-purple-500 hover:bg-purple-600"
          />
        )}
        {hasArtwork && (
          <DocRow
            label="Proof PDF (vector — fully editable)"
            icon={FileText}
            iconClass="text-indigo-600"
            href={`${API_URL}/api/enhanced-orders/${order.id}/proof-pdf`}
            btnClass="bg-indigo-500 hover:bg-indigo-600"
          />
        )}
        {hasArtwork && (
          <DocRow
            label="Print File (production-ready, with bleed)"
            icon={FileText}
            iconClass="text-rose-600"
            href={`${API_URL}/api/enhanced-orders/${order.id}/print-file`}
            btnClass="bg-rose-600 hover:bg-rose-700"
          />
        )}
        {hasArtwork && (order.items || []).length > 1 && (
          <DocRow
            label="All Print Files (ZIP — every item)"
            icon={FileText}
            iconClass="text-rose-700"
            href={`${API_URL}/api/enhanced-orders/${order.id}/print-files-zip`}
            btnClass="bg-rose-700 hover:bg-rose-800"
            testId="print-files-zip-btn"
          />
        )}
        {!hasAny && !hasArtwork && (
          <p className="text-sm text-gray-500 italic">No documents generated yet</p>
        )}
        {/* iter114s.92 — Regenerate Order Confirmation. For retrofitting
            pre-iter114s.91 orders whose snapshot was generated before
            the atomic Freightcom-quote save shipped (those PDFs show
            "Shipping: TBD"). With iter114s.93's dynamic download
            endpoint, this just patches the freightcom_quote on the
            order; the very next download reflects the new rate. */}
        {order.id && order.status && order.status !== 'draft' && (
          <RegenerateConfirmationButton order={order} />
        )}
      </div>
    </div>
  );
};

const RegenerateConfirmationButton = ({ order }) => {
  const [busy, setBusy] = React.useState(false);
  const regenerate = async () => {
    if (busy) return;
    if (!window.confirm(
      'Regenerate the Order Confirmation PDF using current order data?\n\n' +
      'If this order has no saved Freightcom rate, the new PDF will still show ' +
      'Shipping: TBD. To inject a manual rate, paste a Freightcom payload at the next prompt ' +
      '(or cancel and re-quote first).'
    )) return;
    const rateStr = window.prompt(
      'Optional: paste shipping override as JSON, or leave blank.\n\n' +
      'Example:  {"selected_rate":{"carrier_name":"GLS","service_level":"Ground","total_price":8.92,"currency":"CAD"}}',
      ''
    );
    let payload = {};
    if (rateStr && rateStr.trim()) {
      try {
        payload = { freightcom_quote: JSON.parse(rateStr.trim()) };
      } catch (e) {
        toast.error('Invalid JSON — paste a single object or leave blank.');
        return;
      }
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/orders/${order.id}/regenerate-confirmation`,
        payload,
        { withCredentials: true }
      );
      const patched = res.data?.freightcom_patched ? ' (shipping rate updated)' : '';
      toast.success(`Order Confirmation regenerated${patched}.`);
      // The download link reads from order.invoice_id which now points
      // at the new file. Easiest UX: hard-reload the page so the modal
      // picks up the new invoice_id on the next open.
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(
        `Regenerate failed: ${e?.response?.data?.detail || e?.message || 'unknown'}`
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center justify-between bg-white p-3 rounded border border-dashed border-amber-300 mt-2">
      <div className="flex items-center gap-2">
        <RefreshCw className={`w-4 h-4 text-amber-600 ${busy ? 'animate-spin' : ''}`} />
        <div>
          <div className="text-sm font-medium">Regenerate Order Confirmation</div>
          <div className="text-[10px] text-gray-500">
            Retrofit shipping/tax for older orders
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={regenerate}
        disabled={busy}
        data-testid="regenerate-confirmation"
        className="text-xs px-3 py-1 rounded text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50 transition-colors whitespace-nowrap"
      >
        {busy ? 'Regenerating…' : 'Regenerate'}
      </button>
    </div>
  );
};

const CustomerFiles = ({ order }) => {
  const hasArtwork = order.artwork_files && order.artwork_files.length > 0;
  const hasPO = !!order.purchase_order_file_id;
  return (
    <div className="bg-yellow-50 rounded-lg p-4 border-l-4 border-yellow-500">
      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <Upload className="w-5 h-5 text-yellow-600" />
        Customer Uploaded Files
      </h3>
      <div className="space-y-2">
        {hasArtwork && (
          <div>
            <p className="text-xs text-gray-600 font-semibold mb-2">ARTWORK FILES:</p>
            {order.artwork_files.map((fileId, idx) => (
              <div key={fileId} className="flex items-center justify-between bg-white p-2 rounded mb-1">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm">Artwork {idx + 1}</span>
                </div>
                <a
                  href={`${API_URL}/api/files/download/${fileId}`}
                  download
                  className="text-xs px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                >
                  Download
                </a>
              </div>
            ))}
          </div>
        )}
        {hasPO && (
          <div>
            <p className="text-xs text-gray-600 font-semibold mb-2">PURCHASE ORDER:</p>
            <DocRow
              label="Purchase Order PDF"
              icon={FileText}
              iconClass="text-orange-600"
              href={`${API_URL}/api/files/download/${order.purchase_order_file_id}`}
              btnClass="bg-orange-500 hover:bg-orange-600"
            />
          </div>
        )}
        {!hasArtwork && !hasPO && (
          <p className="text-sm text-gray-500 italic">No files uploaded by customer</p>
        )}
      </div>
    </div>
  );
};

const ArtworkInstructions = ({ order }) => {
  const final = (order.artwork_instructions || '').trim();
  const original = (order.artwork_instructions_original || '').trim();
  if (!final && !original) return null;

  // Show the diff only when the customer revised their notes between
  // Page 2 (original) and the Review page (final). Identical strings
  // collapse into a single "Notes" panel — no audit-trail to surface.
  const hasRevision = original && final && original !== final;

  return (
    <div
      className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500"
      data-testid="admin-artwork-instructions"
    >
      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-blue-600" />
        Customer Artwork Notes
        {hasRevision && (
          <span
            className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium flex items-center gap-1"
            data-testid="admin-artwork-notes-revised-badge"
          >
            <History className="w-3 h-3" />
            Revised by customer
          </span>
        )}
      </h3>
      {hasRevision ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">
              Original (Page 2)
            </p>
            <p
              className="bg-white rounded p-3 border border-gray-200 text-gray-700 whitespace-pre-wrap"
              data-testid="admin-artwork-notes-original"
            >
              {original}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold mb-1">
              Final (Review page)
            </p>
            <p
              className="bg-white rounded p-3 border border-blue-300 text-gray-800 whitespace-pre-wrap"
              data-testid="admin-artwork-notes-final"
            >
              {final}
            </p>
          </div>
        </div>
      ) : (
        <p
          className="bg-white rounded p-3 border border-gray-200 text-gray-700 whitespace-pre-wrap text-sm"
          data-testid="admin-artwork-notes-final"
        >
          {final || original}
        </p>
      )}
    </div>
  );
};

const OrderItems = ({ items }) => (  <div className="bg-gray-50 rounded-lg p-4">
    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
      <Package className="w-5 h-5 text-[#4A7C59]" />
      Order Items
    </h3>
    <div className="space-y-2">
      {items?.map((item, idx) => (
        <div key={item.id || `item-${idx}`} className="bg-white p-3 rounded border border-gray-200">
          <p className="font-medium text-gray-800">{item.product_name || item.product_type}</p>
          <div className="text-xs text-gray-600 mt-1 space-y-1">
            <p>Format: {item.format} | Material: {item.material || 'N/A'}</p>
            <p>Size: {item.size || 'N/A'} | Quantity: {item.quantity}</p>
            <p className="font-semibold text-[#4A7C59]">
              Price: ${item.price?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ShippingInfo = ({ shipping }) => {
  if (!shipping) return null;
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <Truck className="w-5 h-5 text-[#4A7C59]" />
        Shipping Information
      </h3>
      <div className="text-sm space-y-1 text-gray-700">
        <p className="font-semibold">{shipping.company_name}</p>
        <p>{shipping.contact_name}</p>
        <p>{shipping.address_line1}</p>
        {shipping.address_line2 && <p>{shipping.address_line2}</p>}
        <p>{shipping.city}, {shipping.state_province} {shipping.postal_code}</p>
        <p>{shipping.country}</p>
        <p className="pt-2">{shipping.email}</p>
        <p>{shipping.phone}</p>
      </div>
    </div>
  );
};

const FreightcomQuoteCard = ({ quote, orderId }) => {
  // iter114s.31 — admin can hit "Re-quote with same parameters" to
  // pull fresh live rates against the saved package profile + addresses
  // (no manual data entry). The button shows the top-3 rates inline so
  // the admin can compare before booking, without leaving the modal.
  const [reQuoting, setReQuoting] = React.useState(false);
  const [freshRates, setFreshRates] = React.useState(null);
  const [reQuoteError, setReQuoteError] = React.useState(null);

  if (!quote) return null;
  const rate = quote.selected_rate || {};
  const pkg = quote.package || {};

  const handleReQuote = async () => {
    setReQuoting(true);
    setReQuoteError(null);
    setFreshRates(null);
    try {
      // Empty body → backend hydrates from order.freightcom_quote snapshot.
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/shipping/requote/${orderId}`,
        null,
        { withCredentials: true },
      );
      setFreshRates(r.data?.rates || []);
    } catch (e) {
      setReQuoteError(e?.response?.data?.detail || 'Re-quote request failed.');
    } finally {
      setReQuoting(false);
    }
  };

  return (
    <div
      className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-500"
      data-testid="admin-freightcom-quote-card"
    >
      <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
        <Truck className="w-5 h-5 text-amber-600" />
        Freightcom Quote — locked
      </h3>
      <p className="text-xs text-gray-600 mb-3">
        Log into Freightcom and look up quote named{' '}
        <span className="font-mono font-bold text-amber-700" data-testid="admin-freightcom-quote-name">
          {quote.quote_name}
        </span>{' '}
        to book the shipment, or re-quote below for fresh live rates.
      </p>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-xs text-gray-500 uppercase">Carrier</p>
          <p className="font-semibold text-gray-800">{rate.carrier_name}</p>
          <p className="text-gray-600">{rate.service_level}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase">Rate</p>
          <p className="font-bold text-xl text-[#2F5D3A]">
            ${Number(rate.total_price || 0).toFixed(2)} <span className="text-xs font-normal">{rate.currency || 'CAD'}</span>
          </p>
          {rate.estimated_delivery_days != null && (
            <p className="text-xs text-gray-600">{rate.estimated_delivery_days} business days</p>
          )}
        </div>
        <div className="col-span-2 text-xs text-gray-600 border-t border-amber-200 pt-2">
          Package: {pkg.length_cm}×{pkg.width_cm}×{pkg.height_cm} cm · {Number(pkg.weight_kg || 0).toFixed(2)} kg
          {pkg.profile && <span className="text-gray-400"> ({pkg.profile})</span>}
          {quote.saved_at && (
            <span className="ml-2 text-gray-400">· saved {new Date(quote.saved_at).toLocaleString()}</span>
          )}
          {quote.mock_mode && (
            <span className="ml-2 text-amber-700 font-semibold">(preview — mock)</span>
          )}
        </div>
      </div>

      {/* iter114s.31 — Re-quote button */}
      <div className="mt-3 pt-3 border-t border-amber-200">
        <Button
          onClick={handleReQuote}
          disabled={reQuoting}
          variant="outline"
          className="border-amber-500 text-amber-800 hover:bg-amber-100 w-full"
          data-testid="admin-freightcom-requote-btn"
        >
          {reQuoting ? 'Fetching fresh rates…' : 'Re-quote with same parameters'}
        </Button>
        {reQuoteError && (
          <p className="text-xs text-red-700 mt-2" data-testid="admin-freightcom-requote-error">
            {reQuoteError}
          </p>
        )}
        {Array.isArray(freshRates) && freshRates.length > 0 && (
          <div
            className="mt-3 bg-white rounded border border-amber-200 divide-y divide-amber-100"
            data-testid="admin-freightcom-requote-rates"
          >
            <div className="px-3 py-2 text-xs font-semibold text-gray-700 bg-amber-50">
              Fresh live rates (top 3) — package + addresses unchanged
            </div>
            {freshRates.slice(0, 3).map((fr, idx) => (
              <div
                key={`${fr.carrier_name}-${fr.service_code}-${idx}`}
                className="px-3 py-2 flex items-center justify-between text-sm"
                data-testid={`admin-freightcom-requote-rate-${idx}`}
              >
                <div>
                  <span className="font-semibold text-gray-800">{fr.carrier_name}</span>
                  <span className="text-gray-600 ml-2">{fr.service_level}</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-[#2F5D3A]">
                    ${Number(fr.total_price || 0).toFixed(2)} {fr.currency || 'CAD'}
                  </span>
                  {fr.estimated_delivery_days != null && (
                    <div className="text-xs text-gray-500">
                      {fr.estimated_delivery_days} business days
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {Array.isArray(freshRates) && freshRates.length === 0 && (
          <p className="text-xs text-gray-600 mt-2 italic">
            Freightcom returned no live rates for this profile right now. Try again or adjust the package on the order.
          </p>
        )}
      </div>
    </div>
  );
};

const ProofUpload = ({ proofFile, onFileChange, onUpload }) => (
  <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-500">
    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
      <Upload className="w-5 h-5 text-green-600" />
      Manual Proof Upload (Revised Proof)
    </h3>
    <p className="text-xs text-gray-600 mb-3">
      Upload a revised proof if customer rejected the auto-generated one
    </p>
    <input
      type="file"
      accept=".pdf"
      onChange={(e) => onFileChange(e.target.files[0])}
      className="w-full px-3 py-2 border border-gray-300 rounded mb-2 text-sm"
      data-testid="admin-proof-upload-input"
    />
    <Button
      onClick={onUpload}
      disabled={!proofFile}
      className="bg-green-500 hover:bg-green-600 text-white w-full"
      data-testid="admin-proof-upload-btn"
    >
      <Upload className="w-4 h-4 mr-2 inline" />
      Upload Revised Proof
    </Button>
  </div>
);

const StatusEditor = ({ orderStatus, trackingNumber, onStatusChange, onTrackingChange }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
      <Edit className="w-5 h-5 text-[#4A7C59]" />
      Update Order Status
    </h3>
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
        <select
          value={orderStatus}
          onChange={(e) => onStatusChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded"
          data-testid="admin-status-select"
        >
          {STATUS_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tracking Number (Optional)
        </label>
        <input
          type="text"
          value={trackingNumber}
          onChange={(e) => onTrackingChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded"
          placeholder="Enter tracking number..."
          data-testid="admin-tracking-input"
        />
      </div>
    </div>
  </div>
);

export const OrderManagementModal = ({
  open,
  order,
  proofFile,
  trackingNumber,
  orderStatus,
  onClose,
  onProofFileChange,
  onUploadProof,
  onTrackingChange,
  onStatusChange,
  onUpdateStatus,
}) => {
  if (!open || !order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        data-testid="admin-order-modal"
      >
        <div className="bg-[#4A7C59] text-white px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Order Management</h2>
            <button onClick={onClose} className="text-white hover:text-gray-200" data-testid="admin-order-modal-close">
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-sm text-green-100 mt-1">Order #{order.order_number}</p>
        </div>

        <div className="p-6 space-y-6">
          <OrderSummary order={order} />
          <GeneratedDocs order={order} />
          <CustomerFiles order={order} />
          <ArtworkInstructions order={order} />
          <OrderItems items={order.items} />
          <ShippingInfo shipping={order.shipping_info} />
          <FreightcomQuoteCard quote={order.freightcom_quote} orderId={order.id} />
          <ProofUpload
            proofFile={proofFile}
            onFileChange={onProofFileChange}
            onUpload={() => onUploadProof(order.id)}
          />
          <StatusEditor
            orderStatus={orderStatus}
            trackingNumber={trackingNumber}
            onStatusChange={onStatusChange}
            onTrackingChange={onTrackingChange}
          />

          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={() => onUpdateStatus(order.id)}
              className="flex-1 bg-[#4A7C59] hover:bg-[#3d6849] text-white py-3"
              data-testid="admin-update-order-btn"
            >
              <Save className="w-4 h-4 mr-2 inline" />
              Update Order
            </Button>
            <Button
              onClick={onClose}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3"
              data-testid="admin-order-modal-cancel"
            >
              <X className="w-4 h-4 mr-2 inline" />
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
